import { Roles } from 'consts/authConsts'
import { isUserOfRole } from 'utils/authUtils'

export const UserNoteVisiblities = {
  Public: null,
  HI: 'HI'
}

export const AttachmentTypes = {
  Image: 'image'
}

export const NoteTypes = {
  NeedsAttention: 'needsAttention',
  NeedsAttentionResolved: 'needsAttentionResolved'
}

const noteTypeOf = note => {
  try {
    return JSON.parse(note?.metadata || '{}').noteType
  } catch {
    return undefined
  }
}

/** The patient's open Needs Attention: its needsAttention tag, if one exists and is not deleted. */
export const findOpenNeedsAttentionTag = patientTags =>
  patientTags?.find(patientTag => !patientTag?._deleted && patientTag?.tag?.value === 'needsAttention')

/**
 * Whether a Needs Attention note is the one behind the patient's open Needs Attention (GR-10746): there
 * is an open needsAttention tag, and this is the newest needsAttention note (notes are sorted oldest
 * first; the backend writes the note right after applying the tag). Resolving leaves the original
 * note's type unchanged, so without this every past request would stay editable.
 */
export const isLiveNeedsAttentionNote = ({ noteId, notes = [], hasOpenNeedsAttention }) =>
  !!hasOpenNeedsAttention &&
  [...notes].reverse().find(note => noteTypeOf(note) === NoteTypes.NeedsAttention)?.id === noteId

/**
 * The body of a Needs Attention note after its HI text changes. The backend writes the body as
 * "Attention required (<reason>)" plus ": <text>" when there is text, and the panel shows the text from
 * metadata.hiNote, so only that tail is swapped. A body in another shape is kept as is.
 */
export const replaceNeedsAttentionNoteText = ({ body = '', previousNote = '', note = '' }) => {
  const suffix = previousNote ? `: ${previousNote}` : ''
  if (suffix && !body.endsWith(suffix)) {
    return body
  }

  const prefix = body.slice(0, body.length - suffix.length)
  return note ? `${prefix}: ${note}` : prefix
}

export const buildSearchUserNotesFilter = ({ patientGrinUserId }) => {
  const isAdmin = isUserOfRole([Roles.Admin])
  const isHI = isUserOfRole([Roles.HI])

  return {
    grinUserId: { eq: patientGrinUserId },
    or: [
      {
        visibility: { exists: false } // If visibility is not set, it means the note is visible to all
      },
      isAdmin && {
        visibility: { exists: true } // Admins can see all notes regardless of visibility
      },
      isHI && {
        visibility: { eq: UserNoteVisiblities.HI }
      }
    ].filter(Boolean)
  }
}

const parseNoteMetadata = note => {
  try {
    return JSON.parse(note?.metadata || '{}')
  } catch {
    return {}
  }
}

/**
 * The id of the note that opened the patient's current Needs Attention: the newest needsAttention note.
 * Notes are sorted oldest first. Only meaningful while the patient still has the needsAttention tag,
 * because a resolve without a reply leaves no resolved note behind.
 */
export const getActiveNeedsAttentionNoteId = (notes = []) =>
  [...notes].reverse().find(note => parseNoteMetadata(note).noteType === NoteTypes.NeedsAttention)?.id ?? null
